/**
 * The dataset endpoints, one function per step of the import conversation.
 *
 *   1. `uploadDataset`     POST /datasets                    store, detect, suggest
 *   2. `setColumnMapping`  PUT  /datasets/:id/mapping         confirm → import starts
 *   3. `fetchDatasetStatus` GET /datasets/:id/status          the poll target
 *
 * Every request body and every response is described by a contract from
 * `@wend/shared`; nothing is re-declared here. The import is asynchronous by
 * design, so step 2 answers with a dataset that is only `processing` — the
 * counts and the rejected-row report arrive later, through step 3.
 */
import {
  datasetListResponseSchema,
  datasetResponseSchema,
  datasetStatusResponseSchema,
  rejectedRowReportSchema,
  setColumnMappingRequestSchema,
  uploadDatasetResponseSchema,
  type ColumnMapping,
  type Dataset,
  type DatasetListQuery,
  type DatasetListResponse,
  type DatasetStatusResponse,
  type RejectedRowReport,
  type UploadDatasetResponse,
} from '@wend/shared';

import { apiRequest } from './client';

/** Ids come from the API, but a path segment is never interpolated raw. */
function datasetPath(datasetId: string, suffix = ''): string {
  return `/datasets/${encodeURIComponent(datasetId)}${suffix}`;
}

export async function listDatasets(
  query: DatasetListQuery,
  signal?: AbortSignal,
): Promise<DatasetListResponse> {
  return apiRequest({
    path: '/datasets',
    query: {
      page: String(query.page),
      pageSize: String(query.pageSize),
      sort: query.sort,
      direction: query.direction,
      // An empty search is not a filter for "nothing"; it is no filter at all.
      ...(query.search === undefined || query.search === '' ? {} : { search: query.search }),
    },
    schema: datasetListResponseSchema,
    signal,
  });
}

/** One dataset, with the detected columns and the mapping that was confirmed. */
export async function fetchDataset(datasetId: string, signal?: AbortSignal): Promise<Dataset> {
  const response = await apiRequest({
    path: datasetPath(datasetId),
    schema: datasetResponseSchema,
    signal,
  });
  return response.dataset;
}

/**
 * The cheap poll target. Deliberately smaller than the full dataset, because it
 * is hit on a timer for as long as `status` is `pending` or `processing`.
 */
export async function fetchDatasetStatus(
  datasetId: string,
  signal?: AbortSignal,
): Promise<DatasetStatusResponse> {
  return apiRequest({
    path: datasetPath(datasetId, '/status'),
    schema: datasetStatusResponseSchema,
    signal,
  });
}

/** Every row that did not become a contact, with the reason it did not. */
export async function fetchRejectedRows(
  datasetId: string,
  signal?: AbortSignal,
): Promise<RejectedRowReport> {
  return apiRequest({
    path: datasetPath(datasetId, '/rejected-rows'),
    schema: rejectedRowReportSchema,
    signal,
  });
}

export interface DatasetUpload {
  file: File;
  /** Defaults, server-side, to the file name without its extension. */
  name?: string;
}

/**
 * Posts the file as `multipart/form-data` and answers with the pending dataset,
 * its detected columns and a *suggested* mapping.
 *
 * Nothing is imported by this call. The suggestion is a starting point for the
 * user to correct, which is why it travels beside the dataset rather than in
 * `dataset.columnMapping` — that field stays null until a person confirms one.
 */
export async function uploadDataset(
  input: DatasetUpload,
  signal?: AbortSignal,
): Promise<UploadDatasetResponse> {
  const form = new FormData();
  // The field name is part of the contract: the API reads `file` and rejects an
  // upload that arrives under any other name.
  form.append('file', input.file, input.file.name);
  if (input.name !== undefined && input.name.trim() !== '') {
    form.append('name', input.name.trim());
  }

  return apiRequest({
    path: '/datasets',
    method: 'POST',
    body: form,
    schema: uploadDatasetResponseSchema,
    signal,
  });
}

/**
 * Confirming the mapping is what starts the import: the dataset comes back in
 * `processing` and the caller polls `fetchDatasetStatus` from there.
 */
export async function setColumnMapping(
  datasetId: string,
  mapping: ColumnMapping,
): Promise<Dataset> {
  const response = await apiRequest({
    path: datasetPath(datasetId, '/mapping'),
    method: 'PUT',
    // Re-validates against the same contract the API validates against, so a
    // mapping that could never be accepted never leaves the browser.
    body: setColumnMappingRequestSchema.parse({ mapping }),
    schema: datasetResponseSchema,
  });
  return response.dataset;
}
